/**
 * Компонент диалога экспорта данных KLS
 * 
 * Функциональность:
 * - Выбор типа сущности для экспорта
 * - Выбор формата экспорта (CSV, XLSX, JSON)
 * - Настройка фильтров (тип квалификатора, квалификатор, даты, удаленные)
 * - Скачивание шаблона для импорта
 * - Экспорт данных
 */

import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { klsImportExportApi, qualTypesApi, qualsApi } from "@/services/kls-api.service";
import { useQuery } from "@tanstack/react-query";
import type {
  ExportFormat,
  EntityType,
  QualType,
  QualWithType,
} from "@/types/api-v2";
import { Download, FileText, Loader2 } from "lucide-react";

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entityType?: EntityType; // Предустановленный тип сущности
  qualTypeId?: number; // Предустановленный тип квалификатора
  qualId?: number; // Предустановленный квалификатор
}

export default function ExportDialog({
  isOpen,
  onClose,
  entityType: defaultEntityType,
  qualTypeId: defaultQualTypeId,
  qualId: defaultQualId,
}: ExportDialogProps) {
  const { toast } = useToast();
  
  const [format, setFormat] = useState<ExportFormat>("csv");
  const [entityType, setEntityType] = useState<EntityType>(defaultEntityType || "qual_type");
  const [qualTypeId, setQualTypeId] = useState<number | undefined>(defaultQualTypeId);
  const [qualId, setQualId] = useState<number | undefined>(defaultQualId);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  
  // Загрузка типов квалификаторов
  const { data: qualTypes = [] } = useQuery<QualType[]>({
    queryKey: ["qual-types"],
    queryFn: () => qualTypesApi.list({ include_deleted: false }),
  });
  
  // Загрузка квалификаторов (если выбран тип)
  const { data: quals = [] } = useQuery<QualWithType[]>({
    queryKey: ["quals", qualTypeId],
    queryFn: () => qualsApi.list({ qual_type_id: qualTypeId, include_deleted: false }),
    enabled: !!qualTypeId && (entityType === "qual" || entityType === "kls"),
  });
  
  // Мутация экспорта
  const exportMutation = useMutation({
    mutationFn: async () => {
      const params: any = {
        include_deleted: includeDeleted,
      };
      
      if (qualTypeId) {
        params.qual_type_id = qualTypeId;
      }
      
      if (qualId) {
        params.qual_id = qualId;
      }
      
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      
      if (dateTo) {
        params.date_to = dateTo;
      }
      
      const blob = await klsImportExportApi.export(format, entityType, params);
      
      // Создание ссылки для скачивания
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kls_${entityType}_export.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      toast({
        title: "Экспорт завершен",
        description: "Файл успешно скачан",
      });
      handleClose();
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка экспорта",
        description: error?.message || "Не удалось выполнить экспорт",
        variant: "destructive",
      });
    },
  });
  
  // Мутация скачивания шаблона
  const templateMutation = useMutation({
    mutationFn: async () => {
      const blob = await klsImportExportApi.downloadTemplate(format, entityType);
      
      // Создание ссылки для скачивания
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kls_${entityType}_template.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      toast({
        title: "Шаблон скачан",
        description: "Файл шаблона успешно скачан",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error?.message || "Не удалось скачать шаблон",
        variant: "destructive",
      });
    },
  });
  
  const handleClose = () => {
    setFormat("csv");
    setEntityType(defaultEntityType || "qual_type");
    setQualTypeId(defaultQualTypeId);
    setQualId(defaultQualId);
    setIncludeDeleted(false);
    setDateFrom("");
    setDateTo("");
    onClose();
  };
  
  const handleExport = () => {
    exportMutation.mutate();
  };
  
  const handleDownloadTemplate = () => {
    templateMutation.mutate();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Экспорт данных</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Выбор формата и типа сущности */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Формат экспорта</Label>
              <Select value={format} onValueChange={(v) => setFormat(v as ExportFormat)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Тип сущности</Label>
              <Select value={entityType} onValueChange={(v) => setEntityType(v as EntityType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qual_type">Типы квалификаторов</SelectItem>
                  <SelectItem value="qual">Квалификаторы</SelectItem>
                  <SelectItem value="kls">Разделы классификаторов</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Фильтры */}
          {(entityType === "qual" || entityType === "kls") && (
            <div className="space-y-2">
              <Label>Тип квалификатора</Label>
              <Select
                value={qualTypeId?.toString() || "all"}
                onValueChange={(v) => {
                  setQualTypeId(v === "all" ? undefined : parseInt(v));
                  setQualId(undefined); // Сброс квалификатора при смене типа
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Все типы" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все типы квалификаторов</SelectItem>
                  {qualTypes.map((type) => (
                    <SelectItem key={type.qual_type_id} value={type.qual_type_id.toString()}>
                      {type.qual_type_namef}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {entityType === "kls" && (
            <div className="space-y-2">
              <Label>Квалификатор</Label>
              <Select
                value={qualId?.toString() || "all"}
                onValueChange={(v) => setQualId(v === "all" ? undefined : parseInt(v))}
                disabled={!qualTypeId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={qualTypeId ? "Все квалификаторы" : "Сначала выберите тип"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все квалификаторы</SelectItem>
                  {quals.map((qual) => (
                    <SelectItem key={qual.qual_id} value={qual.qual_id.toString()}>
                      {qual.qual_namef}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Фильтры по дате (только для kls) */}
          {entityType === "kls" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Дата создания (от)</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Дата создания (до)</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          )}
          
          {/* Включить удаленные */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="includeDeleted"
              checked={includeDeleted}
              onCheckedChange={(checked) => setIncludeDeleted(checked === true)}
            />
            <Label htmlFor="includeDeleted" className="cursor-pointer">
              Включать удаленные записи
            </Label>
          </div>
          
          {/* Кнопка скачивания шаблона */}
          <div className="pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={templateMutation.isPending}
              className="w-full"
            >
              {templateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Загрузка...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Скачать шаблон для импорта
                </>
              )}
            </Button>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={exportMutation.isPending}>
            Отмена
          </Button>
          <Button onClick={handleExport} disabled={exportMutation.isPending}>
            {exportMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Экспорт...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Экспортировать
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

